import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Shield,
  FileText,
  Calendar,
  Euro,
  CheckCircle,
  Clock,
  AlertTriangle,
  MapPin,
  Car,
  User,
  Wrench,
  FileWarning,
  Camera,
  History,
  Upload,
  ChevronDown,
  ArrowLeft,
  Mail,
  Download,
  Eye,
  ChevronUp,
  Archive,
  UserCheck,
  ClipboardCheck,
} from 'lucide-react';
import api, { dossierAPI, documentAPI } from '../../api.js';
import { API_BASE_URL, getAuthHeaders } from '../../api/core.js';
import { useFormat } from '../../i18n/format.js';
import { useNavigation } from '../../v3/context/NavigationContext.jsx';
import DossierTimeline from '../common/DossierTimeline';
import WorkflowTransitionModal from '../common/WorkflowTransitionModal';
import DocumentDropZone from '../common/DocumentDropZone.jsx';
import DocumentGallery from '../common/DocumentGallery.jsx';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import { useAuth } from '../../v3/context/AuthContext.jsx';
import { ClaimGarageAssignModal, ClaimRollbackModal } from './ClaimCorrectionModals.jsx';

// Rôles autorisés à défaire une étape — miroir de CLAIM_ROLLBACK_ROLES côté
// serveur (backend/routes/workflow_routes.mjs). L'UI cache le bouton, le
// backend refuse la requête : les deux, jamais l'un seul.
const ROLLBACK_ROLES = ['admin', 'org_admin', 'manager', 'fleet_manager'];

function ClaimDetailModal({ claim, onClose, onUpdate, onMessage, onWorkflowAction, inline = false }) {
  const { money } = useOrgCurrency();
  const { t, fmt } = useFormat();
  const { navigateTo } = useNavigation();
  const { user, isSuperuser } = useAuth();
  const canRollback = isSuperuser === true || ROLLBACK_ROLES.includes(user?.role);
  const [showGarageAssign, setShowGarageAssign] = useState(false);
  const [showRollback, setShowRollback] = useState(false);
  const [confirmingAction, setConfirmingAction] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);

  // Dossier state for timeline
  const [dossier, setDossier] = useState(null);
  const [loadingDossier, setLoadingDossier] = useState(true);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'history'

  // Unified document storage (per docstore.yaml)
  const [showUnifiedUpload, setShowUnifiedUpload] = useState(false);
  const [unifiedDocuments, setUnifiedDocuments] = useState([]);
  const [loadingUnifiedDocs, setLoadingUnifiedDocs] = useState(false);

  // Workflow transition modal state
  const [showTransitionModal, setShowTransitionModal] = useState(false);

  // Liability is now read-only here — set via workflow transition (accuse-recu)

  // Email correspondence state
  const [claimEmails, setClaimEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [expandedEmailId, setExpandedEmailId] = useState(null);

  // Garage/broker/expert enrichment from supplier
  const [garageInfo, setGarageInfo] = useState(null);
  const [expertInfo, setExpertInfo] = useState(null);
  const [brokerInfo, setBrokerInfo] = useState(null);
  const [contractInfo, setContractInfo] = useState(null);

  // Actions dropdown state
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowActionsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load related data
  useEffect(() => {
    const loadRelatedData = async () => {
      try {
        if (claim.vehicle_id) {
          const vRes = await api.vehicle.getById(claim.vehicle_id);
          if (vRes.ok) setVehicleInfo(vRes.data);
        }
        if (claim.driver_id) {
          const dRes = await api.driver.getById(claim.driver_id);
          if (dRes.ok) setDriverInfo(dRes.data);
        }
        // Enrich garage: from supplier_id FK, or fuzzy match by name
        if (claim.garage_supplier_id) {
          const gRes = await api.supplier.getById(claim.garage_supplier_id);
          if (gRes.ok) setGarageInfo(gRes.data);
        } else if (claim.garage_name && !claim.garage_phone) {
          // No FK but has a name without details — try to match from suppliers
          const sRes = await api.supplier.getAll();
          if (sRes.ok) {
            const match = (sRes.data || []).find(s =>
              s.category === 'garage' && s.name?.toLowerCase() === claim.garage_name.toLowerCase()
            );
            if (match) setGarageInfo(match);
          }
        }
        // Enrich expert supplier
        if (claim.expert_supplier_id) {
          const eRes = await api.supplier.getById(claim.expert_supplier_id);
          if (eRes.ok) setExpertInfo(eRes.data);
        }
        // Enrich broker (courtier) — from claim.broker_id or contract.intermediary_id
        let brokerId = claim.broker_id || claim.intermediary_id;
        if (claim.contract_id) {
          try {
            const cRes = await api.insurance.getById(claim.contract_id);
            if (cRes.ok) {
              setContractInfo(cRes.data);
              if (!brokerId && cRes.data?.intermediary_id) {
                brokerId = cRes.data.intermediary_id;
              }
            }
          } catch (e) { /* ignore */ }
        }
        if (brokerId) {
          const bRes = await api.supplier.getById(brokerId);
          if (bRes.ok) setBrokerInfo(bRes.data);
        }
      } catch (err) {
        console.warn('Could not load related data:', err);
      }
    };

    loadRelatedData();
    loadDossier();
    loadUnifiedDocuments();
    loadClaimEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claim.id, claim.vehicle_id, claim.driver_id]);

  // Load dossier (full history/timeline)
  const loadDossier = async () => {
    try {
      setLoadingDossier(true);
      const res = await dossierAPI.getClaim(claim.id);
      if (res.ok) {
        setDossier(res.data);
      }
    } catch (err) {
      console.warn('Could not load dossier:', err);
    } finally {
      setLoadingDossier(false);
    }
  };

  // Load unified documents (new system)
  const loadUnifiedDocuments = async () => {
    if (!claim?.id) return;
    setLoadingUnifiedDocs(true);
    try {
      const result = await documentAPI.getForClaim(`CLM_${String(claim.id).padStart(6, '0')}`);
      if (result.ok) {
        setUnifiedDocuments(result.data || []);
      }
    } catch (err) {
      console.warn('Could not load unified documents:', err);
    } finally {
      setLoadingUnifiedDocs(false);
    }
  };

  // Load generated emails for this claim
  const loadClaimEmails = async () => {
    if (!claim?.id) return;
    setLoadingEmails(true);
    try {
      const res = await fetch(`${API_BASE_URL}/generated_emails?entity_type=claim&entity_id=${claim.id}&_sort=generated_at&_order=desc`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      const allEmails = data?.data || [];
      // Dedup: group by subject, keep the one with a real recipient (not test address)
      const seen = new Map();
      for (const em of allEmails) {
        const key = em.subject || em.id;
        const existing = seen.get(key);
        if (!existing) {
          seen.set(key, em);
        } else if (existing.recipient_email === 'recipient@example.com' && em.recipient_email !== 'recipient@example.com') {
          seen.set(key, em); // prefer real recipient
        }
      }
      setClaimEmails(Array.from(seen.values()));
    } catch (err) {
      console.warn('Could not load claim emails:', err);
    } finally {
      setLoadingEmails(false);
    }
  };

  // Download .eml file for a generated email
  const handleDownloadEml = (email) => {
    const date = new Date(email.generated_at || Date.now()).toUTCString();
    const boundary = `----=_Part_${Date.now()}`;
    let eml = `From: ${email.sender_name || 'Service Flotte'} <${email.sender_email || 'noreply@fleetmanager.local'}>\r\n`;
    eml += `To: ${email.recipient_name ? `${email.recipient_name} <${email.recipient_email}>` : (email.recipient_email || '')}\r\n`;
    eml += `Subject: =?UTF-8?B?${btoa(unescape(encodeURIComponent(email.subject || 'Sans objet')))}?=\r\n`;
    eml += `Date: ${date}\r\n`;
    eml += `MIME-Version: 1.0\r\n`;
    eml += `Content-Type: multipart/alternative; boundary="${boundary}"\r\n`;
    eml += `X-Generated-By: FleetManager\r\n`;
    eml += `X-Template-Code: ${email.template_code || 'N/A'}\r\n`;
    eml += `\r\n`;
    eml += `--${boundary}\r\n`;
    eml += `Content-Type: text/plain; charset="UTF-8"\r\n\r\n`;
    eml += `${email.body_text || ''}\r\n\r\n`;
    if (email.body_html) {
      eml += `--${boundary}\r\n`;
      eml += `Content-Type: text/html; charset="UTF-8"\r\n\r\n`;
      eml += `${email.body_html}\r\n\r\n`;
    }
    eml += `--${boundary}--\r\n`;

    const blob = new Blob([eml], { type: 'message/rfc822' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = email.eml_file_name || `SIN-${claim.id}_email_${email.id}.eml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Download stored .eml from server (falls back to in-memory generation)
  const handleDownloadStoredEml = (email) => {
    if (!email.eml_file_name) {
      handleDownloadEml(email);
      return;
    }
    const storedDoc = unifiedDocuments.find(
      (d) => d.original_filename === email.eml_file_name || d.filename === email.eml_file_name
    );
    if (storedDoc) {
      documentAPI.download(storedDoc.id, storedDoc.original_filename || storedDoc.filename);
    } else {
      handleDownloadEml(email);
    }
  };

  // Download complete claim dossier as ZIP
  const handleDownloadArchive = async () => {
    try {
      onMessage?.({ title: t('claims.messages.archiveTitle'), message: t('claims.messages.archivePreparing'), type: 'info' });
      const res = await fetch(`${API_BASE_URL}/workflow/claim/${claim.id}/archive`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(t('claims.messages.archiveError'));
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SIN-${claim.id}_DOSSIER_COMPLET_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      onMessage?.({ title: t('claims.messages.successTitle'), message: t('claims.messages.archiveDownloaded'), type: 'success' });
    } catch (err) {
      onMessage?.({ title: t('claims.messages.errorTitle'), message: err.message, type: 'error' });
    }
  };

  // Template code → French label
  const templateLabels = {
    CLAIM_DECLARATION: 'Déclaration de sinistre',
    CLAIM_AR_RECEIVED: 'Accusé de réception',
    CLAIM_EXPERT_ASSIGNED: 'Expert désigné',
    CLAIM_EXPERT_REPORT: 'Rapport d\'expert',
    CLAIM_REPAIR_AUTHORIZED: 'Réparation autorisée',
    CLAIM_REQUEST_GARAGE_INVOICE: 'Demande de facture garage',
    CLAIM_REPAIR_COMPLETE: 'Réparation terminée',
    CLAIM_VEI_DECLARED: 'VEI déclarée',
    CLAIM_VEI_DOCS_SENT: 'Documents VEI envoyés',
    CLAIM_CLOSED: 'Clôture du sinistre',
    CLAIM_INDEMNIFIED: 'Indemnisation',
    CLAIM_RELANCE_EXPERT: 'Relance expert',
    CLAIM_RELANCE_AR: 'Relance accusé de réception',
    CLAIM_RELANCE_RAPPORT: 'Relance rapport',
    CLAIM_RELANCE_PEC: 'Relance prise en charge',
    CLAIM_RELANCE_REGLEMENT: 'Relance règlement',
    CLAIM_PEC_REQUEST: 'Demande de prise en charge',
  };

  // Handle unified document upload complete
  const handleUnifiedUploadComplete = uploadedDocs => {
    loadUnifiedDocuments();
    setShowUnifiedUpload(false);
    onMessage?.({
      title: t('claims.messages.successTitle'),
      message: t('claims.detail.docsUploaded', { count: uploadedDocs.length }),
      type: 'success',
    });
  };

  // Legacy states mapping (for backward compatibility)
  const legacyStatesMapping = {
    draft: 'brouillon',
    declared: 'declaration_envoyee',
    acknowledged: 'en_instruction',
    expert_pending: 'expert_pending',
    expertise_realisee: 'expertise_realisee',
    rapport_recu: 'rapport_recu',
    prise_en_charge_demandee: 'prise_en_charge_demandee',
    expert_done: 'rapport_recu', // Legacy expert_done maps to rapport_recu
    repair_authorized: 'en_reparation',
    indemnified: 'attente_indemnisation',
    closed: 'cloture',
    rejected: 'cloture',
  };

  // Normalize status to new workflow
  const normalizedStatus = legacyStatesMapping[claim.status] || claim.status;

  // Check SLA overdue using stored deadline fields (jours ouvrés, computed by backend)
  const isSlaOverdue = (deadlineField) => {
    const deadline = claim[deadlineField];
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  // Expert designation overdue: J+5 ouvrés from AR
  const isExpertOverdue = normalizedStatus === 'en_instruction' && isSlaOverdue('sla_expert_contact_deadline');
  // Expert report overdue: J+10 ouvrés from visit
  const isReportOverdue = (normalizedStatus === 'expert_pending' || normalizedStatus === 'expertise_realisee') && isSlaOverdue('sla_expert_report_deadline');
  // PEC confirmation overdue: J+2 ouvrés from demand
  const isPecOverdue = normalizedStatus === 'prise_en_charge_demandee' && isSlaOverdue('sla_pec_confirmation_deadline');
  // Reglement overdue: J+15 ouvrés from invoice
  const isReglementOverdue = normalizedStatus === 'attente_indemnisation' && isSlaOverdue('sla_reglement_deadline');

  const daysSinceDeclaration = claim.declared_at
    ? Math.floor((new Date() - new Date(claim.declared_at)) / (1000 * 60 * 60 * 24))
    : 0;

  // Get workflow actions based on current state (Fleet Manager workflow)
  const getWorkflowActions = status => {
    // Normalize legacy status
    const normalizedSt = legacyStatesMapping[status] || status;

    // Build en_instruction actions dynamically based on SLA state
    const enInstructionActions = [];
    if (isExpertOverdue) {
      enInstructionActions.push({
        key: 'relance-expert',
        label: t('claims.actions.relanceExpertLate'),
        color: 'red',
        icon: AlertTriangle,
      });
    }
    enInstructionActions.push(
      { key: 'designer-expert', label: t('claims.actions.designerExpert'), color: 'purple', icon: UserCheck },
    );

    const actions = {
      brouillon: [
        {
          key: 'envoyer-declaration',
          label: t('claims.actions.envoyerDeclaration'),
          color: 'yellow',
          icon: FileWarning,
        },
      ],
      declaration_envoyee: [
        { key: 'accuse-recu', label: t('claims.actions.accuseRecu'), color: 'blue', icon: CheckCircle },
        { key: 'retour-brouillon', label: t('claims.actions.retourBrouillon'), color: 'gray', icon: FileText },
      ],
      en_instruction: enInstructionActions,
      expert_pending: [
        { key: 'expertise-realisee', label: t('claims.actions.expertiseRealisee'), color: 'purple', icon: ClipboardCheck },
      ],
      expertise_realisee: [
        { key: 'rapport-expert', label: t('claims.actions.rapportExpert'), color: 'indigo', icon: ClipboardCheck },
        ...(claim.expert_conclusion === 'vei' ? [
          { key: 'declarer-vei', label: t('claims.actions.declarerVeiKo'), color: 'orange', icon: AlertTriangle },
        ] : []),
        ...(isReportOverdue ? [
          { key: 'relance-expert', label: t('claims.actions.relanceReportLate'), color: 'red', icon: AlertTriangle },
        ] : []),
      ],
      rapport_recu: [
        { key: 'demande-pec', label: t('claims.actions.demandePec'), color: 'cyan', icon: Shield },
        { key: 'declarer-vei', label: t('claims.actions.declarerVei'), color: 'orange', icon: AlertTriangle },
      ],
      prise_en_charge_demandee: [
        { key: 'reparation-validee', label: t('claims.actions.reparationValidee'), color: 'emerald', icon: Wrench },
        { key: 'declarer-vei', label: t('claims.actions.declarerVei'), color: 'orange', icon: AlertTriangle },
        ...(isPecOverdue ? [
          { key: 'relance-expert', label: t('claims.actions.relancePecLate'), color: 'red', icon: AlertTriangle },
        ] : []),
      ],
      en_reparation: [
        { key: 'reparation-terminee', label: t('claims.actions.reparationTerminee'), color: 'purple', icon: Euro },
      ],
      attente_facture_garage: [
        { key: 'facture-garage-recue', label: t('claims.actions.factureGarageRecue'), color: 'emerald', icon: Euro },
        { key: 'relance-facture-garage', label: t('claims.actions.relanceFactureGarage'), color: 'amber', icon: Mail },
      ],
      vei: [
        {
          key: 'documents-vei-transmis',
          label: t('claims.actions.documentsVeiTransmis'),
          color: 'purple',
          icon: Euro,
        },
      ],
      attente_indemnisation: [
        {
          key: 'indemnisation-recue',
          label: t('claims.actions.indemnisationRecue'),
          color: 'emerald',
          icon: CheckCircle,
        },
        ...(isReglementOverdue ? [
          { key: 'relance-expert', label: t('claims.actions.relanceSettlementLate'), color: 'red', icon: AlertTriangle },
        ] : []),
      ],
      // Legacy status actions
      draft: [{ key: 'envoyer-declaration', label: t('claims.actions.envoyerDeclaration'), color: 'yellow', icon: FileWarning }],
      declared: [{ key: 'accuse-recu', label: t('claims.actions.accuseRecuLegacy'), color: 'blue', icon: CheckCircle }],
    };
    return actions[normalizedSt] || actions[status] || [];
  };

  // Actions that need data input go directly to the full transition modal (no simple confirm first)
  const DATA_COLLECTION_ACTIONS = [
    'accuse-recu',         // reference_assureur, franchise, liability
    'designer-expert',     // expert supplier selection
    'expertise-realisee',  // conclusion + montant
    'rapport-expert',      // montant_expertise final
    'demande-pec',         // garage selection
    'reparation-validee',  // repair amount, indemnification
    'reparation-terminee', // invoice amount, vehicle restitution, document upload
    'facture-garage-recue', // invoice amount
  ];

  const handleAction = async action => {
    setConfirmingAction(action);
    if (DATA_COLLECTION_ACTIONS.includes(action.key)) {
      setShowTransitionModal(true);
    }
  };

  const confirmAction = async () => {
    if (!confirmingAction) return;

    try {
      const result = await onWorkflowAction(claim.id, confirmingAction.key);
      setConfirmingAction(null);
      // If the action closed the claim, close the modal
      if (result?.closed) {
        onClose();
      } else {
        onUpdate && onUpdate();
        loadDossier();
      }
    } catch (error) {
      console.error('Workflow action failed:', error);
      onMessage?.({
        title: t('claims.messages.errorTitle'),
        message: error?.message || t('claims.messages.actionExecError'),
        type: 'error',
      });
      setConfirmingAction(null);
    }
  };

  const getButtonColorClasses = colorName => {
    const colors = {
      yellow: 'bg-yellow-500 hover:bg-yellow-600',
      blue: 'bg-blue-500 hover:bg-blue-600',
      purple: 'bg-purple-500 hover:bg-purple-600',
      indigo: 'bg-indigo-500 hover:bg-indigo-600',
      cyan: 'bg-cyan-500 hover:bg-cyan-600',
      green: 'bg-green-500 hover:bg-green-600',
      emerald: 'bg-emerald-500 hover:bg-emerald-600',
      red: 'bg-red-500 hover:bg-red-600',
      amber: 'bg-amber-500 hover:bg-amber-600',
    };
    return colors[colorName] || 'bg-gray-500 hover:bg-gray-600';
  };

  const availableActions = getWorkflowActions(claim.status);

  // Claim type labels
  const claimTypeLabels = {
    collision: 'Collision',
    theft: 'Vol',
    vandalism: 'Vandalisme',
    glass_breakage: 'Bris de Glace',
    fire: 'Incendie',
    natural_disaster: 'Catastrophe Naturelle',
    other: 'Autre',
  };

  // Liability labels (5-level system — determined by insurer, not user)
  const liabilityLabels = {
    user_100: 'Responsable à 100%',
    user_75: 'Responsable à 75%',
    shared_50: 'Responsabilité partagée (50%)',
    user_25: 'Responsable à 25%',
    not_responsible: 'Non responsable (0%)',
    // Legacy mappings
    insured: 'Assuré responsable',
    driver: 'Responsable à 100%',
    third_party: 'Non responsable',
    shared: 'Responsabilité partagée',
    unknown: 'Responsabilité à déterminer',
  };

  /**
   * Single source of truth: liability enum → percentage.
   * The `liability` enum on the claim is the master field (set by broker via workflow).
   * Returns 100 if unknown/unset (conservative: assume full responsibility until broker confirms).
   */
  const getLiabilityPct = (liability) => {
    const map = { user_100: 100, user_75: 75, shared_50: 50, user_25: 25, not_responsible: 0,
      insured: 100, driver: 100, third_party: 0, shared: 50 };
    return map[liability] ?? 100;
  };

  const BROKER_STEPS = [
    {
      id: 'declaration',
      statusMatch: ['brouillon', 'declaration_prete', 'declaration_envoyee'],
      label: t('claims.brokerSteps.declaration.label'),
      doneWhen: c => c.declared_at,
      pendingText: t('claims.brokerSteps.declaration.pending'),
      expectedFields: [
        { key: 'insurer_reference', label: t('claims.brokerSteps.declaration.insurerReference'), value: c => c.insurer_reference },
        { key: 'liability', label: t('claims.brokerSteps.declaration.liability'), value: c => c.liability && c.liability !== 'unknown' ? t(`claims.liability.${c.liability}`, { defaultValue: liabilityLabels[c.liability] }) : null },
      ],
    },
    {
      id: 'instruction',
      statusMatch: ['en_instruction'],
      label: t('claims.brokerSteps.instruction.label'),
      doneWhen: c => c.expert_assigned_at,
      pendingText: t('claims.brokerSteps.instruction.pending'),
      expectedFields: [
        { key: 'expert_name', label: t('claims.brokerSteps.instruction.expertName'), value: c => c.expert_name },
        { key: 'expert_rdv_date', label: t('claims.brokerSteps.instruction.expertRdvDate'), value: c => c.expert_rdv_date },
      ],
    },
    {
      id: 'expertise',
      statusMatch: ['expert_pending', 'expertise_realisee'],
      label: t('claims.brokerSteps.expertise.label'),
      doneWhen: c => c.expert_report_at,
      pendingText: t('claims.brokerSteps.expertise.pending'),
      expectedFields: [
        { key: 'expert_conclusion', label: t('claims.brokerSteps.expertise.expertConclusion'), value: c => c.expert_conclusion && c.expert_conclusion !== 'pending' ? (c.expert_conclusion === 'repairable' ? t('claims.expert.repairable') : t('claims.expert.totalLossShort')) : null },
        { key: 'montant_expertise', label: t('claims.brokerSteps.expertise.amount'), value: c => c.montant_expertise ? `${fmt.number(parseFloat(c.montant_expertise))} \u20AC` : null },
      ],
    },
    {
      id: 'pec',
      statusMatch: ['rapport_recu', 'prise_en_charge_demandee'],
      label: t('claims.brokerSteps.pec.label'),
      doneWhen: c => c.repair_authorized_at,
      pendingText: t('claims.brokerSteps.pec.pending'),
      expectedFields: [
        { key: 'repair_authorized', label: t('claims.brokerSteps.pec.agreement'), value: c => c.repair_authorized_at ? t('claims.brokerSteps.pec.approved') : null },
      ],
    },
    {
      id: 'reparation',
      statusMatch: ['en_reparation', 'attente_facture_garage'],
      label: t('claims.brokerSteps.reparation.label'),
      doneWhen: c => c.montant_facture_garage,
      pendingText: t('claims.brokerSteps.reparation.pending'),
      expectedFields: [
        { key: 'montant_facture_garage', label: t('claims.brokerSteps.reparation.invoiceAmount'), value: c => c.montant_facture_garage ? `${fmt.number(parseFloat(c.montant_facture_garage))} \u20AC` : null },
      ],
    },
    {
      id: 'indemnisation',
      statusMatch: ['attente_indemnisation'],
      label: t('claims.brokerSteps.indemnisation.label'),
      doneWhen: c => c.indemnified_at || c.indemnity_amount,
      pendingText: t('claims.brokerSteps.indemnisation.pending'),
      expectedFields: [
        { key: 'indemnity_amount', label: t('claims.brokerSteps.indemnisation.amount'), value: c => c.indemnity_amount ? `${fmt.number(parseFloat(c.indemnity_amount))} \u20AC` : null },
      ],
    },
  ];


  const formatDate = dateStr => {
    if (!dateStr) return t('common.states.notAvailable');
    return fmt.date(dateStr);
  };

  const formatDateTime = dateStr => {
    if (!dateStr) return t('common.states.notAvailable');
    return fmt.dateTime(dateStr);
  };

  // Devise de l'organisation du sinistre — un seul outil monétaire (utils/formatMoney).
  const formatMoney = amount =>
    (amount === null || amount === undefined ? '-' : money(amount, claim));

  return (
    <div className={inline
      ? ''
      : 'fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end'
    }>
      <div className={inline
        ? 'bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col'
        : 'bg-white shadow-2xl w-full max-w-5xl h-full overflow-hidden flex flex-col animate-slide-in-right'
      }>
        {/* Header with Actions Dropdown */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              {inline && (
                <button
                  onClick={onClose}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                  title={t('claims.detail.backToList')}
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div>
              <h2 className="text-xl font-bold">{t('claims.detail.title', { id: claim.id })}</h2>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-red-100 text-sm">
                  {t(`claims.types.${claim.type}`, { defaultValue: claimTypeLabels[claim.type] || claim.type })} - {formatDate(claim.event_date)}
                </span>
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                    normalizedStatus === 'cloture'
                      ? 'bg-green-400 text-green-900'
                      : normalizedStatus === 'en_reparation'
                        ? 'bg-cyan-400 text-cyan-900'
                        : normalizedStatus === 'attente_indemnisation'
                          ? 'bg-purple-400 text-purple-900'
                          : claim.status === 'rejected'
                            ? 'bg-red-400 text-red-900'
                            : 'bg-yellow-300 text-yellow-900'
                  }`}
                >
                  {t(`claims.status.${normalizedStatus}`, { defaultValue: { brouillon: 'Brouillon', draft: 'Brouillon',
                     declaration_prete: 'À envoyer au courtier',
                     declaration_envoyee: 'Déclaration envoyée', declared: 'Déclaré',
                     en_instruction: 'En instruction', acknowledged: 'Accusé réception',
                     expert_pending: 'Expert désigné', expertise_realisee: 'Expertise réalisée',
                     rapport_recu: 'Rapport expert reçu', expert_done: 'Rapport expert',
                     prise_en_charge_demandee: 'PEC demandée',
                     en_reparation: 'En réparation', repair_authorized: 'Réparation autorisée',
                     attente_facture_garage: 'Attente facture garage',
                     attente_indemnisation: 'Attente indemnisation',
                     vei: 'VEI', offre_vei_recue: 'Offre VEI reçue', vei_conserve: 'VEI — Conservé',
                     indemnified: 'Indemnisé',
                     cloture: 'Clôturé', closed: 'Clôturé',
                     rejected: 'Rejeté',
                  }[normalizedStatus] || claim.status || t('common.states.notAvailable') })}
                </span>
                {/* Duration indicator — "Ouvert depuis X jours" */}
                {(() => {
                  const refDate = claim.event_date || claim.created_at;
                  if (!refDate) return null;
                  const days = Math.floor((new Date() - new Date(refDate)) / (1000 * 60 * 60 * 24));
                  const isClosed = ['cloture', 'closed', 'rejected', 'vei_conserve'].includes(normalizedStatus);
                  const closedDate = claim.closed_at || claim.cloture_at;
                  const totalDays = isClosed && closedDate
                    ? Math.floor((new Date(closedDate) - new Date(refDate)) / (1000 * 60 * 60 * 24))
                    : days;
                  const urgency = totalDays > 90 ? 'bg-red-400/30 text-red-100' : totalDays > 30 ? 'bg-orange-400/30 text-orange-100' : 'bg-white/20 text-red-100';
                  return (
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${urgency}`} title={t('claims.detail.since', { date: formatDate(refDate) })}>
                      <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />
                      {isClosed ? t('claims.detail.processedInDays', { count: totalDays }) : t('claims.detail.openForDays', { count: totalDays })}
                    </span>
                  );
                })()}
              </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!inline && (
                <button onClick={onClose} className="text-white hover:text-gray-200 p-1">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 bg-gray-50 px-6">
          <nav className="flex gap-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'details'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-2" />
              {t('claims.detail.tabDetails')}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition ${
                activeTab === 'history'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <History className="w-4 h-4 inline mr-2" />
              {t('claims.detail.tabHistory')}
              {dossier?.workflowActions?.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                  {dossier.workflowActions.length}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* History Tab - DossierTimeline */}
          {activeTab === 'history' && (
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
              {loadingDossier ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="w-8 h-8 mx-auto mb-3 animate-spin text-gray-300" />
                  <p>{t('claims.detail.historyLoading')}</p>
                </div>
              ) : dossier ? (
                <>
                  {/* Dossier Stats */}
                  {dossier.stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {dossier.stats.daysOpen}
                        </div>
                        <div className="text-xs text-blue-700">{t('claims.detail.statsDaysOpen')}</div>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {dossier.stats.actionsCount}
                        </div>
                        <div className="text-xs text-purple-700">{t('claims.detail.statsActions')}</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {dossier.stats.documentsCount}
                        </div>
                        <div className="text-xs text-green-700">{t('claims.detail.statsDocuments')}</div>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-amber-600">
                          {formatMoney(dossier.stats.totalExpenses ?? 0)}
                        </div>
                        <div className="text-xs text-amber-700">{t('claims.detail.statsExpenses')}</div>
                      </div>
                    </div>
                  )}

                  {/* Timeline */}
                  <DossierTimeline
                    timeline={dossier.timeline || []}
                    workflowActions={dossier.workflowActions || []}
                    documents={dossier.documents || []}
                    dossierType="claim"
                    sortOrder="desc"
                    emptyMessage={t('claims.detail.historyEmpty')}
                  />
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>{t('claims.detail.historyError')}</p>
                </div>
              )}
            </div>
          )}

          {/* Details Tab — 3-column layout (mockup: 3-6-3) */}
          {activeTab === 'details' && (
            <>
              <div className="grid grid-cols-12 gap-4">
                {/* ═══ LEFT COLUMN (3) — Context cards ═══ */}
                <div className="col-span-12 lg:col-span-3 space-y-4">
                  {/* Event Information */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 border-b border-gray-100 pb-2">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                      {t('claims.detail.eventSection')}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">{t('claims.detail.fieldType')}</span>
                        <span className="font-medium">{t(`claims.types.${claim.type}`, { defaultValue: claimTypeLabels[claim.type] || claim.type })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">{t('claims.detail.fieldDate')}</span>
                        <span className="font-medium">{formatDate(claim.event_date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">{t('claims.detail.fieldTime')}</span>
                        <span className="font-medium">{claim.event_time || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">{t('claims.detail.fieldDrivable')}</span>
                        <span className={`font-medium ${claim.drivable ? 'text-green-600' : 'text-red-600'}`}>
                          {claim.drivable === null ? '-' : claim.drivable ? t('common.yes') : t('common.no')}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">{t('claims.detail.fieldLiability')}</span>
                        <span className={`font-medium flex items-center gap-1.5 ${claim.liability && claim.liability !== 'unknown' ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                          {claim.liability && claim.liability !== 'unknown' && (
                            <span className="w-2 h-2 bg-green-500 rounded-full inline-block flex-shrink-0" title={t('claims.detail.receivedFromBroker')} />
                          )}
                          {claim.liability && claim.liability !== 'unknown'
                            ? t(`claims.liability.${claim.liability}`, { defaultValue: liabilityLabels[claim.liability] || claim.liability })
                            : t('claims.liability.pendingAcknowledgement')}
                        </span>
                      </div>
                      {(claim.insurer_reference || claim.reference_assureur) && (
                        <div className="flex justify-between items-center mt-1 pt-1 border-t border-gray-100">
                          <span className="text-gray-500">{t('claims.detail.fieldInsurerReference')}</span>
                          <span className="font-medium text-indigo-700 text-xs flex items-center gap-1.5">
                            <span className="w-2 h-2 bg-green-500 rounded-full inline-block flex-shrink-0" title={t('claims.detail.receivedFromBroker')} />
                            {claim.insurer_reference || claim.reference_assureur}
                          </span>
                        </div>
                      )}
                      {claim.location && (
                        <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-gray-100">
                          <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-gray-600">{claim.location}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {claim.description && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2 border-b border-amber-200 pb-2">
                        <FileText className="w-4 h-4" />
                        {t('claims.detail.descriptionSection')}
                      </h4>
                      <p className="text-sm italic text-gray-700">"{claim.description}"</p>
                    </div>
                  )}

                  {/* Parties Involved */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 border-b border-gray-100 pb-2">
                      <User className="w-4 h-4 text-blue-600" />
                      {t('claims.detail.partiesSection')}
                    </h4>
                    <div className="space-y-2">
                      <div
                        className="p-2 bg-blue-50 rounded cursor-pointer hover:bg-blue-100 hover:shadow-sm transition-all group"
                        onClick={() => claim.vehicle_id && navigateTo('vehicles', { selectedId: claim.vehicle_id, returnTo: 'claims', returnParams: { selectedId: claim.id } })}
                        title={t('claims.detail.viewVehicle')}
                      >
                        <p className="text-xs font-bold text-blue-800 flex items-center justify-between">
                          {t('claims.detail.vehicle')}
                          <span className="text-blue-400 group-hover:text-blue-600 text-xs">{t('claims.detail.viewLink')}</span>
                        </p>
                        <p className="text-sm">
                          {vehicleInfo
                            ? `${vehicleInfo.make} ${vehicleInfo.model} - ${vehicleInfo.license_plate}`
                            : t('claims.detail.vehicleFallback', { id: claim.vehicle_id })}
                        </p>
                      </div>
                      {claim.driver_id && (
                        <div
                          className="p-2 bg-green-50 rounded cursor-pointer hover:bg-green-100 hover:shadow-sm transition-all group"
                          onClick={() => navigateTo('drivers', { selectedId: claim.driver_id, returnTo: 'claims', returnParams: { selectedId: claim.id } })}
                          title={t('claims.detail.viewDriver')}
                        >
                          <p className="text-xs font-bold text-green-800 flex items-center justify-between">
                            {t('claims.detail.driver')}
                            <span className="text-green-400 group-hover:text-green-600 text-xs">{t('claims.detail.viewLink')}</span>
                          </p>
                          <p className="text-sm">
                            {driverInfo
                              ? `${driverInfo.first_name} ${driverInfo.last_name}`
                              : t('claims.detail.driverFallback', { id: claim.driver_id })}
                          </p>
                        </div>
                      )}
                      {claim.contract_id && (
                        <div
                          className="p-2 bg-purple-50 rounded cursor-pointer hover:bg-purple-100 hover:shadow-sm transition-all group"
                          onClick={() => navigateTo('insurance', { tab: 'contracts', contractId: claim.contract_id })}
                          title={t('claims.detail.viewContract')}
                        >
                          <p className="text-xs font-bold text-purple-800 flex items-center justify-between">
                            {t('claims.detail.contract')}
                            <span className="text-purple-400 group-hover:text-purple-600 text-xs">{t('claims.detail.viewLink')}</span>
                          </p>
                          <p className="text-sm font-medium">
                            {contractInfo?.insurer_name || contractInfo?.company_name || t('claims.detail.contractFallback', { id: claim.contract_id })}
                          </p>
                          {(contractInfo?.policy_number || contractInfo?.contract_number) && (
                            <p className="text-xs text-purple-600 mt-0.5">
                              {t('claims.detail.policyNumber', { number: contractInfo.policy_number || contractInfo.contract_number })}
                            </p>
                          )}
                        </div>
                      )}
                      {brokerInfo && (
                        <div className="p-2 bg-amber-50 rounded border border-amber-200">
                          <p className="text-xs font-bold text-amber-800 flex items-center justify-between">
                            {t('claims.detail.broker')}
                            <span className="text-amber-500 text-xs">{t('claims.detail.brokerRecipient')}</span>
                          </p>
                          <p className="text-sm font-medium">{brokerInfo.name}</p>
                          {(brokerInfo.contact_email || brokerInfo.email) && (
                            <p className="text-xs text-amber-700 mt-0.5">
                              ✉ {brokerInfo.contact_email || brokerInfo.email}
                            </p>
                          )}
                          {brokerInfo.phone && (
                            <p className="text-xs text-amber-600 mt-0.5">☎ {brokerInfo.phone}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ═══ CENTER COLUMN (6) — Work area ═══ */}
                <div className="col-span-12 lg:col-span-6 space-y-4">
                  {/* Timeline of Dates */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3 border-b border-gray-100 pb-2">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-indigo-600" />
                        {t('claims.detail.chronologySection')}
                      </h4>
                      {/* Actions Dropdown — inside Chronologie card.
                          Le recul d'étape s'affiche même sans action disponible :
                          un dossier clôturé par erreur doit pouvoir être rouvert. */}
                      {(availableActions.length > 0 || canRollback) && (
                        <div className="relative" ref={dropdownRef}>
                          <button
                            onClick={() => setShowActionsDropdown(!showActionsDropdown)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg text-sm font-medium text-indigo-700 transition-colors"
                          >
                            {t('claims.detail.actionsButton')}
                            <ChevronDown
                              size={14}
                              className={`transition-transform ${showActionsDropdown ? 'rotate-180' : ''}`}
                            />
                          </button>
                          {showActionsDropdown && (
                            <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                              {availableActions.map(action => {
                                const Icon = action.icon;
                                return (
                                  <button
                                    key={action.key}
                                    onClick={() => {
                                      setShowActionsDropdown(false);
                                      handleAction(action);
                                    }}
                                    className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 ${
                                      action.color === 'red' ? 'text-red-600'
                                        : action.color === 'orange' ? 'text-orange-600'
                                        : action.color === 'green' || action.color === 'emerald' ? 'text-green-600'
                                        : action.color === 'blue' ? 'text-blue-600'
                                        : action.color === 'purple' ? 'text-purple-600'
                                        : action.color === 'cyan' ? 'text-cyan-600'
                                        : action.color === 'yellow' ? 'text-yellow-600'
                                        : 'text-gray-700'
                                    }`}
                                  >
                                    <Icon size={16} />
                                    {action.label}
                                  </button>
                                );
                              })}
                              <div className="border-t border-gray-100 mt-1 pt-1">
                                {availableActions.length > 0 && (
                                  <button
                                    onClick={() => {
                                      setShowActionsDropdown(false);
                                      setConfirmingAction(availableActions[0]);
                                      setShowTransitionModal(true);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 text-gray-600"
                                  >
                                    <FileText size={16} />
                                    {t('claims.detail.advancedTransition')}
                                  </button>
                                )}
                                {canRollback && (
                                  <button
                                    onClick={() => {
                                      setShowActionsDropdown(false);
                                      setShowRollback(true);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-amber-50 text-amber-700"
                                  >
                                    <ArrowLeft size={16} />
                                    {t('claims.rollback.action')}
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center text-xs">
                      {[
                        { id: 'declared', label: t('claims.timeline.declared'), date: claim.declared_at, color: 'yellow' },
                        { id: 'acknowledged', label: t('claims.timeline.acknowledged'), date: claim.acknowledged_at, color: 'blue' },
                        { id: 'expert', label: t('claims.timeline.expert'), date: claim.expert_assigned_at, color: 'purple' },
                        { id: 'report', label: t('claims.timeline.report'), date: claim.expert_report_at, color: 'indigo',
                          subtitle: !claim.expert_report_at && claim.montant_expertise > 0
                            ? `~${money(claim.montant_expertise, claim)}`
                            : null },
                        { id: 'repair', label: t('claims.timeline.repair'), date: claim.repair_authorized_at, color: 'cyan' },
                        { id: 'repairDone', label: t('claims.timeline.repairDone'), date: claim.reparation_terminee_at, color: 'teal',
                          subtitle: claim.vehicule_restitue_at
                            ? (claim.km_restitution
                              ? t('claims.timeline.returnedWithKm', { date: fmt.date(claim.vehicule_restitue_at), km: fmt.number(parseInt(claim.km_restitution)) })
                              : t('claims.timeline.returned', { date: fmt.date(claim.vehicule_restitue_at) }))
                            : null },
                        { id: 'invoice', label: t('claims.timeline.invoice'), date: claim.montant_facture_garage ? claim.reparation_terminee_at : null, color: 'amber', active: claim.status === 'attente_facture_garage' },
                        { id: 'indemnified', label: t('claims.timeline.indemnified'), date: claim.indemnified_at, color: 'green' },
                        { id: 'closed', label: t('claims.timeline.closed'), date: claim.closed_at, color: 'emerald' },
                      ].map((step) => (
                        <div
                          key={step.id}
                          className={`p-2 border rounded ${
                            step.date
                              ? `bg-${step.color}-50 border-${step.color}-200 text-${step.color}-700 font-bold`
                              : 'bg-gray-50 text-gray-400 italic'
                          }`}
                        >
                          <div className="font-medium">{step.label}</div>
                          <div>{step.date ? fmt.date(step.date, { day: '2-digit', month: '2-digit', year: '2-digit' }) : t('common.states.notAvailable')}</div>
                          {step.subtitle && (
                            <div className={`text-[10px] italic mt-0.5 ${step.date ? `text-${step.color}-500` : 'text-purple-500'}`}>{step.subtitle}</div>
                          )}
                        </div>
                      ))}
                      {/* Archive button — fills the 8th cell */}
                      <button
                        onClick={handleDownloadArchive}
                        disabled={!claim.closed_at}
                        className={`p-2 border rounded flex flex-col items-center justify-center gap-1 transition-colors ${
                          claim.closed_at
                            ? 'bg-violet-50 border-violet-300 text-violet-700 hover:bg-violet-100 cursor-pointer font-bold'
                            : 'bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed'
                        }`}
                        title={claim.closed_at ? t('claims.timeline.archiveAvailable') : t('claims.timeline.archiveUnavailable')}
                      >
                        <Archive className="w-4 h-4" />
                        <div className="font-medium">{t('claims.timeline.archive')}</div>
                      </button>
                    </div>
                  </div>

                  {/* Ball Owner — Who acts next? */}
                  {(() => {
                    const BALL_OWNER = {
                      brouillon:                { actor: 'fleet_manager', action: t('claims.ballOwner.brouillon') },
                      declaration_prete:        { actor: 'fleet_manager', action: t('claims.ballOwner.declaration_prete') },
                      declaration_envoyee:      { actor: 'broker',        action: t('claims.ballOwner.declaration_envoyee') },
                      en_instruction:           { actor: 'broker',        action: t('claims.ballOwner.en_instruction') },
                      expert_pending:           { actor: 'expert',        action: t('claims.ballOwner.expert_pending') },
                      expertise_realisee:       { actor: 'expert',        action: t('claims.ballOwner.expertise_realisee') },
                      rapport_recu:             { actor: 'fleet_manager', action: t('claims.ballOwner.rapport_recu') },
                      prise_en_charge_demandee: { actor: 'insurer',       action: t('claims.ballOwner.prise_en_charge_demandee') },
                      en_reparation:            { actor: 'garage',        action: t('claims.ballOwner.en_reparation') },
                      attente_facture_garage:   { actor: 'garage',        action: t('claims.ballOwner.attente_facture_garage') },
                      vei:                      { actor: 'insurer',       action: t('claims.ballOwner.vei') },
                      offre_vei_recue:          { actor: 'fleet_manager', action: t('claims.ballOwner.offre_vei_recue') },
                      attente_indemnisation:    { actor: 'insurer',       action: t('claims.ballOwner.attente_indemnisation') },
                    };
                    const ACTOR_STYLES = {
                      fleet_manager: { bg: 'bg-blue-50',   border: 'border-blue-300',   text: 'text-blue-800',   icon: '👤' },
                      broker:        { bg: 'bg-amber-50',  border: 'border-amber-300',  text: 'text-amber-800',  icon: '🤝' },
                      expert:        { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-800', icon: '🔍' },
                      garage:        { bg: 'bg-cyan-50',   border: 'border-cyan-300',   text: 'text-cyan-800',   icon: '🔧' },
                      insurer:       { bg: 'bg-red-50',    border: 'border-red-300',    text: 'text-red-800',    icon: '🏢' },
                    };
                    const owner = BALL_OWNER[claim.status];
                    if (!owner?.actor) return null;
                    const style = ACTOR_STYLES[owner.actor];
                    return (
                      <div className={`${style.bg} border ${style.border} rounded-lg px-4 py-3 flex items-center gap-3`}>
                        <span className="text-lg">{style.icon}</span>
                        <div className="flex-1 min-w-0">
                          <span className={`text-sm font-semibold ${style.text}`}>
                            {t('claims.ballOwner.waitingFor', { actor: t(`claims.actors.${owner.actor}`) })}
                          </span>
                          <p className={`text-xs ${style.text} opacity-75 mt-0.5`}>
                            {owner.action}
                          </p>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Suivi Courtier — Compact: current pending action + achievement counter */}
                  {(() => {
                    const allStatuses = BROKER_STEPS.flatMap(s => s.statusMatch);
                    const currentIdx = allStatuses.indexOf(claim.status);
                    const currentStep = BROKER_STEPS.find(s => s.statusMatch.includes(claim.status));
                    const completedCount = BROKER_STEPS.filter((s, idx) => {
                      const stepEndIdx = BROKER_STEPS.slice(0, idx).flatMap(x => x.statusMatch).length + s.statusMatch.length - 1;
                      return s.doneWhen(claim) || currentIdx > stepEndIdx;
                    }).length;
                    const isClosed = ['cloture', 'closed', 'rejected'].includes(claim.status);

                    const allFields = BROKER_STEPS.flatMap(s => s.expectedFields);
                    const receivedCount = allFields.filter(f => f.value(claim)).length;
                    const totalFields = allFields.length;

                    if (isClosed) return null;

                    // Special card for declaration_envoyee: prominent "send declaration" action
                    const isDeclarationPending = ['declaration_prete'].includes(claim.status);
                    const declarationEmail = claimEmails.find(e =>
                      e.template_code === 'CLAIM_DECLARATION' || (e.eml_file_name && e.eml_file_name.includes('CLAIM_DECLARATION'))
                    );

                    if (isDeclarationPending) {
                      return (
                        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Mail className="w-5 h-5 text-blue-600" />
                            <span className="text-sm font-bold text-blue-800">{t('claims.detail.sendDeclarationTitle')}</span>
                          </div>
                          <p className="text-xs text-blue-700 mb-3">
                            {t('claims.detail.sendDeclarationHint')}
                          </p>
                          <button
                            onClick={async () => {
                              try {
                                const brokerEmail = brokerInfo?.contact_email || brokerInfo?.email || '';
                                const brokerName = brokerInfo?.name || '';
                                const url = `${API_BASE_URL}/email_templates/download-eml?entity_type=claim&entity_id=${claim.id}&template_code=CLAIM_DECLARATION&email=${encodeURIComponent(brokerEmail)}&name=${encodeURIComponent(brokerName)}`;
                                const res = await fetch(url, { headers: getAuthHeaders() });
                                if (!res.ok) throw new Error(t('claims.messages.declarationDownloadError'));
                                const blob = await res.blob();
                                const a = document.createElement('a');
                                a.href = window.URL.createObjectURL(blob);
                                const disposition = res.headers.get('content-disposition');
                                const filename = disposition?.match(/filename="(.+)"/)?.[1] || `declaration_${claim.id}.eml`;
                                a.download = filename;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(a.href);
                                document.body.removeChild(a);
                                // Confirm sending → transition to declaration_envoyee (SLA starts now)
                                try {
                                  await onWorkflowAction(claim.id, 'confirmer-envoi', {});
                                  onUpdate && onUpdate();
                                  loadDossier();
                                } catch (confirmErr) {
                                  console.warn('confirmer-envoi failed (non-blocking):', confirmErr.message);
                                }
                              } catch (err) {
                                console.error('Download .eml failed:', err);
                                onMessage?.({ type: 'error', text: t('claims.messages.declarationDownloadFailed') });
                              }
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-colors"
                          >
                            <Mail className="w-4 h-4" />
                            {t('claims.detail.openInMailClient')}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-amber-800">
                                {currentStep?.label || t('claims.brokerSteps.fallbackTitle')}
                              </span>
                              <span className="text-xs text-amber-600">{t('claims.brokerSteps.stepsProgress', { done: completedCount, total: BROKER_STEPS.length })}</span>
                            </div>
                            {currentStep?.pendingText && (
                              <p className="text-xs text-amber-700 mt-0.5 italic">{currentStep.pendingText}</p>
                            )}
                            {currentStep && (
                              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
                                {currentStep.expectedFields.map(f => {
                                  const val = f.value(claim);
                                  return val ? null : (
                                    <span key={f.key} className="text-xs text-amber-600">
                                      → {f.label}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            {receivedCount > 0 && (
                              <div className="mt-2 flex items-center gap-2">
                                <div className="flex-1 bg-amber-200 rounded-full h-1.5">
                                  <div
                                    className="bg-green-500 h-1.5 rounded-full transition-all duration-500"
                                    style={{ width: `${(receivedCount / totalFields) * 100}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium text-green-700 whitespace-nowrap">
                                  {t('claims.brokerSteps.fieldsReceived', { received: receivedCount, total: totalFields })}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Documents Section */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3 border-b border-gray-100 pb-2">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Camera className="w-4 h-4 text-blue-600" />
                        {t('claims.detail.documentsSection')}
                      </h4>
                      <button
                        onClick={() => setShowUnifiedUpload(!showUnifiedUpload)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          showUnifiedUpload
                            ? 'bg-gray-200 text-gray-700'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {showUnifiedUpload ? t('common.actions.close') : t('claims.detail.addDocument')}
                      </button>
                    </div>

                    {showUnifiedUpload && (
                      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <DocumentDropZone
                          scope="CLAIM"
                          referenceId={`CLM_${String(claim.id).padStart(6, '0')}`}
                          allowedTypes={[
                            'ACCIDENT_DECLARATION', 'CLAIM_PHOTOS', 'DRIVER_STATEMENT',
                            'EXPERT_REPORT', 'REPAIR_INVOICE', 'REPAIR_QUOTE',
                            'CLAIM_CORRESPONDENCE', 'SETTLEMENT_DOCUMENT',
                          ]}
                          onUploadComplete={handleUnifiedUploadComplete}
                          onError={err => onMessage?.({ title: t('claims.messages.errorTitle'), message: err.message, type: 'error' })}
                        />
                      </div>
                    )}

                    <DocumentGallery
                      scope="CLAIM"
                      referenceId={`CLM_${String(claim.id).padStart(6, '0')}`}
                      documents={unifiedDocuments}
                      loading={loadingUnifiedDocs}
                      onRefresh={loadUnifiedDocuments}
                      groupByType={true}
                      showExpiring={false}
                      onDocumentClick={doc => documentAPI.download(doc.id, doc.original_filename || doc.filename)}
                      onDelete={() => loadUnifiedDocuments()}
                      hideArchived
                    />
                  </div>

                  {/* ═══ Correspondance — Email Timeline ═══ */}
              <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-violet-600" />
                    {t('claims.correspondence.section')}
                    {claimEmails.length > 0 && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-violet-100 text-violet-700 rounded-full">
                        {claimEmails.length}
                      </span>
                    )}
                  </h4>
                </div>

                {loadingEmails ? (
                  <div className="text-sm text-gray-500 py-4 text-center">{t('common.states.loading')}</div>
                ) : claimEmails.length === 0 ? (
                  <div className="text-sm text-gray-400 py-6 text-center">
                    <Mail className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    {t('claims.correspondence.empty')}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {claimEmails.map((email) => {
                      const isExpanded = expandedEmailId === email.id;
                      const label = t(`claims.emailTemplates.${email.template_code}`, { defaultValue: templateLabels[email.template_code] || email.template_code || t('claims.emailTemplates.fallback') });
                      const emailDate = email.generated_at || email.created_at;
                      const dateStr = emailDate ? fmt.dateTime(emailDate) : '-';

                      return (
                        <div key={email.id} className="border border-gray-200 rounded-lg overflow-hidden">
                          {/* Email row header */}
                          <div
                            className="flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                            onClick={() => setExpandedEmailId(isExpanded ? null : email.id)}
                          >
                            <div className="flex-shrink-0">
                              <div className={`w-2 h-2 rounded-full ${email.sent_at ? 'bg-green-500' : 'bg-amber-400'}`}
                                title={email.sent_at ? t('claims.correspondence.sent') : t('claims.correspondence.generatedNotSent')}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-gray-900">{label}</span>
                                <span className="text-xs text-gray-400">—</span>
                                <span className="text-xs text-gray-500 font-medium">{dateStr}</span>
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                → {email.recipient_email || t('claims.correspondence.noRecipient')}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {email.eml_file_name && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDownloadStoredEml(email); }}
                                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title={t('claims.correspondence.openInOutlook')}
                                >
                                  <Mail className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDownloadEml(email); }}
                                className="p-1.5 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded transition-colors"
                                title={t('claims.correspondence.downloadEml')}
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                          </div>

                          {/* Expanded email preview */}
                          {isExpanded && (
                            <div className="border-t border-gray-200 p-4 bg-white">
                              <div className="text-xs text-gray-500 mb-2 space-y-1">
                                <div><span className="font-medium">{t('claims.correspondence.subject')}</span> {email.subject}</div>
                                <div><span className="font-medium">{t('claims.correspondence.to')}</span> {email.recipient_name ? `${email.recipient_name} <${email.recipient_email}>` : email.recipient_email}</div>
                                {email.eml_file_name && (
                                  <div><span className="font-medium">{t('claims.correspondence.file')}</span> {email.eml_file_name}</div>
                                )}
                              </div>
                              <div className="mt-3 p-3 bg-gray-50 rounded border text-sm text-gray-700 max-h-48 overflow-y-auto whitespace-pre-wrap">
                                {email.body_text || t('claims.correspondence.noContent')}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
                </div>

                {/* ═══ RIGHT COLUMN (3) — Financial + Garage ═══ */}
                <div className="col-span-12 lg:col-span-3 space-y-4">
                  {/* Financial Details — prominent */}
                  <div className="bg-white border border-gray-200 border-l-4 border-l-green-500 rounded-lg p-4">
                    <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2 border-b border-gray-100 pb-2">
                      <Euro className="w-4 h-4" />
                      {t('claims.finance.section')}
                    </h4>
                    {/* Main amount: expert evaluation takes precedence over estimate */}
                    {claim.montant_expertise != null && parseFloat(claim.montant_expertise) > 0 ? (
                      <>
                        <div className="text-center py-3">
                          <span className="text-3xl font-bold text-indigo-700">
                            {formatMoney(claim.montant_expertise)}
                          </span>
                          <p className="text-xs text-indigo-600 uppercase mt-1 font-semibold">{t('claims.finance.expertAmountLabel')}</p>
                        </div>
                        {claim.estimated_amount != null && (
                          <div className="flex justify-between text-xs text-gray-400 border-t pt-2 mt-1">
                            <span>{t('claims.finance.initialEstimate')}</span>
                            <span className="line-through">{formatMoney(claim.estimated_amount)}</span>
                          </div>
                        )}
                      </>
                    ) : claim.estimated_amount != null && parseFloat(claim.estimated_amount) > 0 ? (
                      <div className="text-center py-3">
                        <span className="text-3xl font-bold text-gray-900">
                          {formatMoney(claim.estimated_amount)}
                        </span>
                        <p className="text-xs text-gray-500 uppercase mt-1">{t('claims.finance.estimatedAmountLabel')}</p>
                      </div>
                    ) : (
                      <div className="text-center py-3">
                        <span className="text-lg text-gray-400 italic">
                          {['brouillon', 'draft', 'declaration_prete', 'declaration_envoyee'].includes(normalizedStatus)
                            ? t('claims.finance.awaitingDeclaration')
                            : ['en_instruction', 'expert_pending'].includes(normalizedStatus)
                            ? t('claims.finance.awaitingExpertise')
                            : normalizedStatus === 'expertise_realisee'
                            ? t('claims.finance.awaitingReport')
                            : t('claims.finance.noAmount')}
                        </span>
                      </div>
                    )}
                    {/* Computed financial breakdown */}
                    {(() => {
                      // Best reference amount: garage invoice > expert > estimate
                      const refAmount = parseFloat(claim.montant_facture_garage) || parseFloat(claim.montant_expertise) || parseFloat(claim.estimated_amount) || 0;
                      const contractualFranchise = parseFloat(claim.franchise_amount) || 0;
                      // Effective franchise = contractual × liability%. Single source: liability enum
                      const liabPct = getLiabilityPct(claim.liability);
                      const franchise = Math.round(contractualFranchise * liabPct / 100 * 100) / 100;
                      const tvaRate = parseFloat(claim.tva_rate) || 20.0;
                      // Prefer stored HT/TVA when available (set at indemnisation-recue), else compute
                      const refHt = claim.montant_ht ? parseFloat(claim.montant_ht) : Math.round((refAmount / (1 + tvaRate / 100)) * 100) / 100;
                      const tva = claim.montant_tva ? parseFloat(claim.montant_tva) : Math.round((refAmount - refHt) * 100) / 100;
                      const computedIndemnity = Math.max(0, Math.round((refHt - franchise) * 100) / 100);
                      const resteACharge = Math.round((franchise + tva) * 100) / 100;
                      const hasGarageInvoice = claim.montant_facture_garage != null && parseFloat(claim.montant_facture_garage) > 0;

                      return (
                        <div className="border-t pt-2 mt-2 space-y-1.5 text-xs">
                          {/* Garage invoice amount if available */}
                          {hasGarageInvoice && (
                            <>
                              <div className="flex justify-between">
                                <span className="text-gray-600 font-semibold">{t('claims.finance.garageInvoice')}</span>
                                <span className="font-bold text-gray-900">{formatMoney(claim.montant_facture_garage)}</span>
                              </div>
                              {claim.montant_expertise > 0 && (() => {
                                const deviation = ((parseFloat(claim.montant_facture_garage) - parseFloat(claim.montant_expertise)) / parseFloat(claim.montant_expertise)) * 100;
                                return deviation > 10 ? (
                                  <div className="bg-red-50 border border-red-200 rounded px-2 py-1 text-xs text-red-700 flex items-center gap-1">
                                    <span>⚠️</span>
                                    <span>{t('claims.finance.deviationWarning', { percent: Math.round(deviation), amount: formatMoney(claim.montant_expertise) })}</span>
                                  </div>
                                ) : null;
                              })()}
                            </>
                          )}

                          {/* HT / TVA breakdown */}
                          {refAmount > 0 && (
                            <>
                              <div className="flex justify-between text-gray-400">
                                <span>{t('claims.finance.amountExclVat')}</span>
                                <span>{formatMoney(refHt)}</span>
                              </div>
                              <div className="flex justify-between text-gray-400">
                                <span>{t('claims.finance.vat', { rate: tvaRate })}</span>
                                <span>{formatMoney(tva)}</span>
                              </div>
                            </>
                          )}

                          {/* Franchise (adjusted by liability %) */}
                          {contractualFranchise > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">
                                {liabPct < 100 ? t('claims.finance.deductibleWithPct', { pct: liabPct }) : t('claims.finance.deductible')}
                              </span>
                              <span className="font-semibold text-orange-600">
                                {formatMoney(franchise)}
                                {liabPct < 100 && franchise !== contractualFranchise && (
                                  <span className="text-gray-400 line-through ml-1 font-normal text-[10px]">{formatMoney(contractualFranchise)}</span>
                                )}
                              </span>
                            </div>
                          )}

                          {/* Indemnity (computed or from DB) */}
                          <div className="flex justify-between border-t pt-1.5 mt-1.5">
                            <span className="text-gray-600 font-semibold">{t('claims.finance.insurancePayout')}</span>
                            <span className="font-bold text-green-600 text-sm">
                              {formatMoney(claim.indemnity_amount || computedIndemnity)}
                            </span>
                          </div>

                          {/* Indemnification recipient */}
                          {claim.indemnification_recipient && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">{t('claims.finance.paidTo')}</span>
                              <span className={`font-bold text-sm ${claim.indemnification_recipient === 'garage' ? 'text-cyan-600' : 'text-amber-600'}`}>
                                {claim.indemnification_recipient === 'garage' ? t('claims.finance.paidToGarage') : t('claims.finance.paidToInsured')}
                              </span>
                            </div>
                          )}

                          {/* Reste à charge — the key number for the user */}
                          {refAmount > 0 && franchise > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-2">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-bold text-amber-900">{t('claims.finance.remainingCost')}</span>
                                <span className="text-lg font-bold text-amber-700">{formatMoney(resteACharge)}</span>
                              </div>
                              <p className="text-xs text-amber-600 mt-1">
                                {t('claims.finance.remainingCostDetail', { deductible: formatMoney(franchise), rate: tvaRate, vat: formatMoney(tva) })}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Garage / Réparateur */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center justify-between gap-2 border-b border-gray-100 pb-2">
                      <span className="flex items-center gap-2">
                        <Wrench className="w-4 h-4 text-gray-600" />
                        {t('claims.garage.section')}
                      </span>
                      {/* Le garage se connaît rarement le jour de la déclaration :
                          il doit rester modifiable tant que le dossier est ouvert. */}
                      <button
                        type="button"
                        onClick={() => setShowGarageAssign(true)}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {(claim.garage_supplier_id || claim.garage_name)
                          ? t('claims.garage.assignChange')
                          : t('claims.garage.assignAdd')}
                      </button>
                    </h4>
                    <div className="space-y-1.5">
                      {(() => {
                        // Supplier card is source of truth; claim fields are fallback only
                        const gName = garageInfo?.name || claim.garage_name || t('claims.garage.notProvided');
                        const gAddress = garageInfo ? [garageInfo.address, garageInfo.postal_code, garageInfo.city].filter(Boolean).join(', ') : claim.garage_address;
                        const gPhone = garageInfo?.phone || claim.garage_phone;
                        const gEmail = garageInfo?.email || claim.garage_email;
                        return (
                          <>
                            <p className="font-bold text-sm">{gName}</p>
                            {gAddress && <p className="text-xs text-gray-500">{gAddress}</p>}
                            {gPhone && <p className="text-xs text-blue-600 cursor-pointer hover:underline">{gPhone}</p>}
                            {gEmail && <p className="text-xs text-blue-600 cursor-pointer hover:underline">{gEmail}</p>}
                            {!gAddress && !gPhone && !gEmail && gName !== t('claims.garage.notProvided') && (
                              <p className="text-xs text-gray-400 italic">{t('claims.garage.noContactDetails')}</p>
                            )}
                          </>
                        );
                      })()}
                      <div className="mt-2 pt-2 border-t border-gray-100 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">{t('claims.garage.insurerReference')}</span>
                          <span className="font-medium">{claim.insurer_reference || '-'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expert Désigné par la Compagnie */}
                  {(claim.expert_supplier_id || claim.expert_name || claim.expert_assigned_at) && (
                    <div className="bg-white border border-purple-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 border-b border-gray-100 pb-2">
                        <UserCheck className="w-4 h-4 text-purple-600" />
                        {t('claims.expert.section')}
                      </h4>
                      <div className="space-y-1.5">
                        {(() => {
                          const eName = expertInfo?.name || claim.expert_name || t('claims.garage.notProvided');
                          const eAddress = expertInfo ? [expertInfo.address, expertInfo.postal_code, expertInfo.city].filter(Boolean).join(', ') : null;
                          const ePhone = expertInfo?.phone || claim.expert_phone;
                          const eEmail = expertInfo?.email || claim.expert_email;
                          return (
                            <>
                              <p className="font-bold text-sm">{eName}</p>
                              {eAddress && <p className="text-xs text-gray-500">{eAddress}</p>}
                              {ePhone && <p className="text-xs text-blue-600 cursor-pointer hover:underline">{ePhone}</p>}
                              {eEmail && <p className="text-xs text-blue-600 cursor-pointer hover:underline">{eEmail}</p>}
                            </>
                          );
                        })()}
                        <div className="mt-2 pt-2 border-t border-gray-100 text-xs space-y-1">
                          {claim.expert_assigned_at && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">{t('claims.expert.assignedOn')}</span>
                              <span className="font-medium">{fmt.date(claim.expert_assigned_at)}</span>
                            </div>
                          )}
                          {claim.expert_rdv_date && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">{t('claims.expert.garageVisit')}</span>
                              <span className="font-medium text-purple-700">{fmt.date(claim.expert_rdv_date)}</span>
                            </div>
                          )}
                          {claim.expert_report_at && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">{t('claims.expert.reportReceived')}</span>
                              <span className="font-medium">{fmt.date(claim.expert_report_at)}</span>
                            </div>
                          )}
                          {claim.expert_conclusion && claim.expert_conclusion !== 'pending' && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">{t('claims.expert.conclusion')}</span>
                              <span className={`font-bold ${claim.expert_conclusion === 'repairable' ? 'text-green-600' : 'text-red-600'}`}>
                                {claim.expert_conclusion === 'repairable' ? t('claims.expert.repairable') : t('claims.expert.totalLoss')}
                              </span>
                            </div>
                          )}
                          {claim.montant_expertise != null && claim.montant_expertise > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">{t('claims.expert.amount')}</span>
                              <span className="font-bold text-gray-900">{formatMoney(claim.montant_expertise)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </div>{/* close grid-cols-12 */}
            </>
          )}
        </div>

        {/* Workflow Transition Modal */}
        <WorkflowTransitionModal
          isOpen={showTransitionModal}
          onClose={() => setShowTransitionModal(false)}
          onConfirm={async transitionData => {
            try {
              const action = confirmingAction?.key;
              if (action) {
                await onWorkflowAction(claim.id, action, transitionData);
              }
              setConfirmingAction(null);
              setShowTransitionModal(false);
              onUpdate && onUpdate();
              loadDossier(); // Refresh the dossier timeline
            } catch (error) {
              console.error('Workflow transition failed:', error);
              throw error;
            }
          }}
          workflowType="claim"
          currentStatus={claim.status}
          entityId={claim.id}
          entityReference={t('claims.detail.title', { id: claim.id })}
          entityName={claim.description?.substring(0, 50)}
          contextData={{
            montant_expertise: claim.montant_expertise,
            montant_expertise_initial: claim.montant_expertise_initial,
            estimated_amount: claim.estimated_amount,
            garage_supplier_id: claim.garage_supplier_id,
            garage_name: claim.garage_name,
            // Le rendez-vous d'expertise : sans lui, le modal ne peut pas
            // signaler qu'on constate une visite qui n'a pas encore eu lieu.
            expert_rdv_date: claim.expert_rdv_date,
          }}
        />

        {/* Corrections : garage affectable à tout moment, recul d'étape tracé */}
        <ClaimGarageAssignModal
          claim={claim}
          isOpen={showGarageAssign}
          onClose={() => setShowGarageAssign(false)}
          onDone={() => { onUpdate && onUpdate(); loadDossier(); }}
          onMessage={onMessage}
        />
        <ClaimRollbackModal
          claim={claim}
          isOpen={showRollback}
          onClose={() => setShowRollback(false)}
          onDone={() => { onUpdate && onUpdate(); loadDossier(); }}
          onMessage={onMessage}
        />

        {/* Simple Confirmation Dialog — only for pure status-change actions (no data input needed) */}
        {confirmingAction && !showTransitionModal && (
          <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('claims.detail.confirmActionTitle')}</h3>
              <p className="text-gray-700 mb-6">
                {t('claims.detail.confirmActionText', { action: confirmingAction.label })}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmingAction(null)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  onClick={() => confirmAction()}
                  className={`flex-1 px-4 py-2 ${getButtonColorClasses(confirmingAction.color)} text-white rounded-lg font-semibold`}
                >
                  {t('common.actions.confirm')}
                </button>
              </div>
              <button
                onClick={() => setShowTransitionModal(true)}
                className="w-full mt-3 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
              >
                {t('claims.detail.advancedOptions')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClaimDetailModal;
